<template>
  <div class="ct-wechsler-shell">
    <div class="ct-config-strip">
      <p class="ct-wechsler-hint">
        Ingresa el <strong>puntaje</strong> de cada subprueba en el recuadro numérico.
      </p>
      <label
        v-for="field in configFields"
        :key="field.var"
        class="ct-config-field"
      >
        <span class="ct-config-label">{{ field.label }}</span>
        <select
          v-model="config[field.var]"
          class="ct-config-select"
          :class="{ 'has-value': config[field.var] }"
        >
          <option
            v-for="opt in field.options"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.text }}
          </option>
        </select>
      </label>
    </div>

    <div class="ct-form-rows wechsler-grid">
      <div
        v-for="(sub, idx) in subtests"
        :key="sub.var"
        class="ct-item-row wechsler-card"
        :class="{
          'has-value': hasScore(sub.var),
          'ct-guide-blink': isGuideActive(idx),
        }"
      >
        <div class="wechsler-card-main">
          <div class="ct-num">{{ idx + 1 }}</div>
          <div class="ct-wechsler-label" :title="sub.label">
            {{ sub.label }}
          </div>
          <label class="ct-score-field">
            <span class="ct-score-tag">Puntaje</span>
            <input
              v-model="scores[sub.var]"
              type="number"
              min="0"
              :max="scoreMax"
              class="ct-score-input"
              placeholder="—"
              inputmode="numeric"
              aria-label="Puntaje bruto"
              @focus="onScoreInteract"
              @mousedown="onScoreInteract"
              @input="onScoreInput(sub)"
            />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  WISC5_SUBTESTS,
  WISC5_CONFIG,
  WISC5_DEFAULTS,
} from "../data/wisc5Schema";
import {
  WAIS4_SUBTESTS,
  WAIS4_CONFIG,
  WAIS4_DEFAULTS,
} from "../data/wais4Schema";
import { submitCorrection, createCorrectionPayload } from "@/services/testCorrectionAdapter";

const SCHEMA_BY_VALUE = {
  11: {
    subtests: WISC5_SUBTESTS,
    configFields: WISC5_CONFIG,
    defaults: WISC5_DEFAULTS,
    requiresAge: true,
  },
  0: {
    subtests: WAIS4_SUBTESTS,
    configFields: WAIS4_CONFIG,
    defaults: WAIS4_DEFAULTS,
    requiresAge: true,
  },
};

function createEmptyScores(subtests) {
  const scores = {};
  subtests.forEach((sub) => {
    scores[sub.var] = "";
  });
  return scores;
}

export default {
  name: "WechslerForm",
  inject: {
    corregirTestWorkspace: { default: null },
  },
  props: {
    testValue: { type: Number, required: true },
    age: { type: String, default: "" },
    initialAnswers: { type: Object, default: null },
  },
  data() {
    const schema = SCHEMA_BY_VALUE[this.testValue] || SCHEMA_BY_VALUE[11];
    return {
      subtests: schema.subtests,
      configFields: schema.configFields,
      requiresAge: schema.requiresAge,
      config: Object.assign({}, schema.defaults),
      scores: createEmptyScores(schema.subtests),
      corrected: false,
      loading: false,
    };
  },
  created() {
    this.hydrate(this.initialAnswers);
  },
  computed: {
    toolbarLabel() {
      return "Corregir test";
    },
    toolbarDisabled() {
      if (this.loading || this.corrected) return true;
      if (this.requiresAge && !this.age) return true;
      return false;
    },
    // "Equivalente" (puntaje escalar) solo existe en 0-19 en el protocolo
    // Wechsler; "Bruto" no tiene un techo natural, así que no se acota.
    isEquivalente() {
      return this.config.tipo === "Equivalente";
    },
    scoreMax() {
      return this.isEquivalente ? 19 : null;
    },
  },
  watch: {
    testValue() {
      this.reset();
    },
    toolbarLabel: { immediate: true, handler: "emitToolbarMeta" },
    toolbarDisabled: { immediate: true, handler: "emitToolbarMeta" },
    loading: { handler: "emitToolbarMeta" },
    age() {
      this.emitToolbarMeta();
    },
    "config.tipo"(tipo) {
      // Reaplica el tope si el usuario cambia a "Equivalente" después de
      // haber cargado puntajes brutos fuera del rango 0-19.
      if (tipo === "Equivalente") {
        this.subtests.forEach((sub) => this.clampScore(sub.var));
      }
    },
  },
  methods: {
    emitToolbarMeta() {
      this.$emit("toolbar-meta", {
        label: this.toolbarLabel,
        disabled: this.toolbarDisabled,
        loading: this.loading,
      });
    },
    hasScore(varName) {
      const val = this.scores[varName];
      return val !== "" && val != null;
    },
    isGuideActive(idx) {
      const ws = this.corregirTestWorkspace;
      return ws && idx === 0 && ws.isFieldGuideActive(1);
    },
    onScoreInteract() {
      const ws = this.corregirTestWorkspace;
      if (ws && ws.dismissFieldGuide) ws.dismissFieldGuide();
    },
    onScoreInput(sub) {
      this.corrected = false;
      if (this.isEquivalente) this.clampScore(sub.var);
    },
    // Fuerza puntaje Equivalente a un natural entre 0 y 19 — el input number
    // no bloquea por sí solo valores fuera de min/max al tipear.
    clampScore(varName) {
      const raw = this.scores[varName];
      if (raw === "" || raw == null) return;
      const num = Math.trunc(Number(raw));
      if (Number.isNaN(num)) {
        this.scores[varName] = "";
        return;
      }
      const clamped = Math.min(19, Math.max(0, num));
      if (String(clamped) !== String(raw)) {
        this.scores[varName] = String(clamped);
      }
    },
    buildPayload() {
      const answers = Object.assign({}, this.config);
      if (this.requiresAge && this.age) {
        answers.edad = this.age;
      }
      this.subtests.forEach((sub) => {
        const raw = this.scores[sub.var];
        answers[sub.var] =
          raw === "" || raw == null ? "" : String(raw);
      });
      return answers;
    },
    // Inversa de buildPayload. Los puntajes se dejan como string porque eso es
    // lo que produce el v-model del input numérico; mezclar tipos haría que
    // clampScore comparara String(clamped) contra un número.
    hydrate(stored) {
      if (!stored) return;
      Object.keys(this.config).forEach((key) => {
        const raw = stored[key];
        if (raw == null || raw === "") return;
        this.$set(this.config, key, String(raw));
      });
      this.subtests.forEach((sub) => {
        const raw = stored[sub.var];
        this.$set(
          this.scores,
          sub.var,
          raw == null || raw === "" ? "" : String(raw)
        );
      });
    },
    async submit() {
      if (this.toolbarDisabled) return;
      this.loading = true;
      try {
        const data = await submitCorrection(
          createCorrectionPayload({
            testValue: this.testValue,
            answers: this.buildPayload(),
          })
        );
        this.corrected = true;
        this.$emit("corrected", data);
      } catch (err) {
        const msg =
          (err && err.message) ||
          "No se pudo corregir el test. Revisa los datos.";
        if (this.$toasted) this.$toasted.error(msg);
      } finally {
        this.loading = false;
      }
    },
    reset() {
      const schema = SCHEMA_BY_VALUE[this.testValue] || SCHEMA_BY_VALUE[11];
      this.subtests = schema.subtests;
      this.configFields = schema.configFields;
      this.requiresAge = schema.requiresAge;
      this.config = Object.assign({}, schema.defaults);
      this.scores = createEmptyScores(schema.subtests);
      this.corrected = false;
      this.loading = false;
    },
  },
};
</script>
